import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { SlugField } from '@components/core-ui/form-elements/SlugField';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalFooter } from '@modals/core-ui/ModalFooter';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { useNameWithSlug } from '@helpers/useNameWithSlug';
import { useSlugAvailability } from '@helpers/useSlugAvailability';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useMutation } from '@tanstack/react-query';
import { TeamsService } from 'app/API';
import React, { useState } from 'react';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';

export const NewTeamModal = ({ onCancel, onSuccess }: TProps) => {
  const [submitError, setSubmitError] = useState('');

  const { call } = useAPI();
  const { updateMe, organization } = useAuthContext();

  const { name, slug, setName, setSlug } = useNameWithSlug();

  const organizationId = organization?.id ?? '';
  const {
    status: slugStatus,
    isAvailable: isSlugAvailable,
    isUnverified: isSlugUnverified,
    retry: retrySlugCheck,
  } = useSlugAvailability({
    slug,
    enabled: !!organizationId,
    checkAvailability: async (value) => {
      const data = await call(() => TeamsService.isSlugAvailable({ oid: organizationId, slug: value }));
      return !!data?.available;
    },
  });

  const createTeamMutation = useMutation({
    mutationFn: () =>
      call(() => TeamsService.create({ requestBody: { name, slug, organizationId } }), { onError: setSubmitError }),
    onMutate: () => setSubmitError(''),
    onError: () => setSubmitError((current) => current || 'Unable to create the team.'),
    onSuccess: (team) => {
      void updateMe({ includeNotifications: false });
      onSuccess(team);
    },
  });

  if (!organization) {
    return null;
  }

  return (
    <ModalWrapper width="400px">
      {createTeamMutation.isPending ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Create a new Team</ModalTitle>
      <Content>
        <div className="workspaceSettings-inputWrapper">
          <div className="inputContainer">
            <label htmlFor="title">Team Name</label>
            <Input
              label="Name"
              name="title"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </div>

          <div className="inputContainer">
            <label htmlFor="slug">Team ID</label>
            <SlugField label="Team ID" value={slug} status={slugStatus} onChange={setSlug} onRetry={retrySlugCheck} />
          </div>

          {submitError && <div className="workspaceSettings-errorMessage largeText">{submitError}</div>}
        </div>
        <ModalFooter>
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button
            disabled={createTeamMutation.isPending || isSlugUnverified || !isSlugAvailable || !(slug && name)}
            onClick={() => createTeamMutation.mutate()}
          >
            Create
          </Button>
        </ModalFooter>
      </Content>
    </ModalWrapper>
  );
};
